import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, Alert, Dropdown, Form, Table, Modal } from 'react-bootstrap';
import {
    Send,
    Bot,
    User,
    CheckCircle,
    AlertCircle,
    RotateCcw,
    MessageCircle,
    Sparkles,
    Paperclip,
    Image,
    FileText,
    Mic,
    Plus,
    Settings,
    Download,
    Copy,
    ThumbsUp,
    ThumbsDown,
    Zap,
    TrendingUp,
    DollarSign,
    Save,
    Edit,
    Eye,
    Calculator,
    Calendar,
    User as UserIcon,
    Building
} from 'lucide-react';
import { io } from 'socket.io-client';
import { useParams, useHistory } from 'react-router-dom';

// Generate stable user ID function
const generateUserId = (companyId) => {
    // Try to get existing user ID from localStorage first
    const storageKey = `ledgy_chat_user_${companyId}`;
    let userId = localStorage.getItem(storageKey);

    if (!userId) {
        // Generate new stable ID and store it
        userId = `user-${companyId}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        localStorage.setItem(storageKey, userId);
    }

    return userId;
};

const BookkeepingChatPage = () => {
    const { companyId } = useParams();
    const history = useHistory();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [socket, setSocket] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
    const [showingExample, setShowingExample] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [pendingVerification, setPendingVerification] = useState(null);
    const [isCreatingVerification, setIsCreatingVerification] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    // Generate stable user ID that persists across component re-renders
    const userIdRef = useRef(generateUserId(companyId));
    const userId = userIdRef.current;

    console.log('🔍 Component render - Using userId:', userId); // Debug log

    const quickStartExamples = [
        {
            title: "Kontorsmaterial",
            message: "Jag köpte kontorsmaterial på Staples för 450 kr med företagskortet",
            icon: "📝"
        },
        {
            title: "Bensinköp",
            message: "Köpte bensin för 800 kr privat på Shell",
            icon: "⛽"
        },
        {
            title: "Lunch",
            message: "Åt lunch för 150 kr på restaurang med mitt privata kort",
            icon: "🍽️"
        },
        {
            title: "Hyra",
            message: "Betalade hyran 15000 kr via bankgiro",
            icon: "🏢"
        },
        {
            title: "Resa",
            message: "Tog taxi till klient för 350 kr",
            icon: "🚕"
        }
    ];

    // Typewriter effect for examples
    useEffect(() => {
        if (!showingExample && messages.length === 0) {
            const showNextExample = () => {
                setShowingExample(true);
                const example = quickStartExamples[currentExampleIndex];
                let currentText = '';
                let charIndex = 0;

                const typeInterval = setInterval(() => {
                    if (charIndex < example.message.length) {
                        currentText += example.message[charIndex];
                        setInputMessage(currentText);
                        charIndex++;
                    } else {
                        clearInterval(typeInterval);
                        setTimeout(() => {
                            setInputMessage('');
                            setShowingExample(false);
                            setCurrentExampleIndex((prev) => (prev + 1) % quickStartExamples.length);
                        }, 2000);
                    }
                }, 50);

                return () => clearInterval(typeInterval);
            };

            const timeoutId = setTimeout(showNextExample, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [currentExampleIndex, showingExample, messages.length]);

    // Initialize socket connection
    useEffect(() => {
        console.log('🔌 Initializing socket connection with userId:', userId);

        const newSocket = io(process.env.REACT_APP_CHAT_URL || 'http://localhost:3004', {
            withCredentials: true
        });

        newSocket.on('connect', () => {
            console.log('✅ Socket connected for userId:', userId);
            setIsConnected(true);
            addMessage('system', '👋 Hej! Jag är Ledgy AI Assistant. Berätta vad du vill bokföra så hjälper jag dig skapa verifikat automatiskt!\n\nDu kan beskriva transaktioner i vanlig text, ladda upp kvitton eller bilder, eller ställa frågor om bokföring.');
        });

        newSocket.on('disconnect', () => {
            console.log('❌ Socket disconnected for userId:', userId);
            setIsConnected(false);
            addMessage('system', 'Anslutning avbruten. Försöker återansluta...', true);
        });

        newSocket.on('chatResponse', (data) => {
            console.log('💬 Received chat response for userId:', userId, data);
            setIsTyping(false);
            addMessage('bot', data.response, false, {
                verification: data.verification,
                suggestions: data.suggestions,
                requiresMoreInfo: data.requiresMoreInfo
            });
        });

        newSocket.on('chatError', (error) => {
            console.log('❌ Received chat error for userId:', userId, error);
            setIsTyping(false);
            addMessage('bot', error.error + (error.details ? `: ${error.details}` : ''), true);
        });

        newSocket.on('conversationReset', (data) => {
            console.log('🔄 Conversation reset for userId:', userId);
            addMessage('system', data.message);
        });

        setSocket(newSocket);

        setTimeout(() => textareaRef.current?.focus(), 100);

        return () => {
            console.log('🔌 Closing socket connection for userId:', userId);
            newSocket.close();
        };
    }, [companyId]);

    // Auto scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    }, [inputMessage]);

    const addMessage = (sender, message, error = false, extra = {}) => {
        const newMessage = {
            id: Date.now().toString(),
            sender,
            message,
            timestamp: new Date(),
            error,
            ...extra
        };
        setMessages(prev => [...prev, newMessage]);
    };

    const sendMessage = () => {
        const text = inputMessage.trim();
        if (!text || !socket || !isConnected) return;

        console.log('📤 Sending message from userId:', userId, 'Message:', text);

        addMessage('user', text);
        setInputMessage('');
        setIsTyping(true);

        socket.emit('chatMessage', {
            userId,
            message: text,
            attachments: attachments
        });

        setAttachments([]);
    };

    const resetConversation = () => {
        if (socket) {
            console.log('🔄 Requesting conversation reset for userId:', userId);
            socket.emit('resetConversation', userId);
            setMessages([]);
            addMessage('system', 'Konversation återställd. Vad vill du bokföra?');
        }
    };

    const startFreshConversation = () => {
        if (socket) {
            localStorage.removeItem(`ledgy_chat_user_${companyId}`);
            const newUserId = generateUserId(companyId);
            userIdRef.current = newUserId;

            console.log('🆕 Starting fresh conversation with new userId:', newUserId);

            socket.emit('resetConversation', newUserId);
            setMessages([]);
            addMessage('system', 'Ny konversation startad. Vad vill du bokföra?');
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        textareaRef.current?.focus();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            id: Date.now() + Math.random(),
            file,
            name: file.name,
            size: file.size,
            type: file.type
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
    };

    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    const handleVerificationProposal = (verification) => {
        setPendingVerification(verification);
        setShowVerificationModal(true);
    };

    const createVerification = async () => {
        if (!pendingVerification) return;

        setIsCreatingVerification(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/verifications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    companyId,
                    ...pendingVerification
                })
            });

            if (!response.ok) {
                throw new Error('Failed to create verification');
            }

            const createdVerification = await response.json();

            setShowVerificationModal(false);
            setPendingVerification(null);

            addMessage('system', `✅ Verifikat ${createdVerification.id} har skapats framgångsrikt!`);

            setTimeout(() => {
                history.push(`/${companyId}/edit-verification/${createdVerification.id}`);
            }, 2000);

        } catch (error) {
            console.error('Error creating verification:', error);
            addMessage('bot', '❌ Ett fel uppstod när verifikatet skulle skapas. Försök igen.', true);
        } finally {
            setIsCreatingVerification(false);
        }
    };

    const handleVerificationView = (verification) => {
        if (verification.id) {
            history.push(`/${companyId}/edit-verification/${verification.id}`);
        } else {
            history.push(`/${companyId}/verifications`);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK'
        }).format(amount);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const calculateVerificationTotals = (transactions) => {
        const debit = transactions
            .filter(t => t.amount > 0)
            .reduce((sum, t) => sum + t.amount, 0);
        const credit = transactions
            .filter(t => t.amount < 0)
            .reduce((sum, t) => sum + Math.abs(t.amount), 0);
        return { debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
    };


    // ...existing code...

    return (
        <div className="container-fluid p-0">
            <style>{`
                /* Chat interface that respects page layout */
                .chat-container {
                    position: relative;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    min-height: 100vh;
                    padding-bottom: 200px; /* Space for fixed input area */
                }
                
                .chat-messages {
                    padding: 2rem;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                }
                
                /* Fixed Input Area - Container based approach */
                .chat-input-wrapper {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    display: flex;
                    justify-content: center;
                    pointer-events: none; /* Allow clicks to pass through to sidebar */
                }
                
                .chat-input-content {
                    width: 100%;
                    max-width: 960px; /* Reasonable max width */
                    background: white;
                    border-top: 1px solid #e2e8f0;
                    padding: 1.5rem 2rem;
                    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
                    pointer-events: auto; /* Re-enable clicks on content */
                    margin: 0 auto;
                    border-radius: 12px 12px 0 0;
                }
                
                /* Responsive adjustments for different screen sizes */
                @media (min-width: 1400px) {
                    .chat-input-content {
                        margin-left: 260px; /* Large sidebar */
                        margin-right: 20px;
                    }
                }
                
                @media (min-width: 1200px) and (max-width: 1399px) {
                    .chat-input-content {
                        margin-left: 240px; /* Medium sidebar */
                        margin-right: 20px;
                    }
                }
                
                @media (min-width: 992px) and (max-width: 1199px) {
                    .chat-input-content {
                        margin-left: 220px; /* Small sidebar */
                        margin-right: 20px;
                    }
                }
                
                @media (min-width: 768px) and (max-width: 991px) {
                    .chat-input-content {
                        margin-left: 20px; /* No sidebar, just padding */
                        margin-right: 20px;
                    }
                }
                
                @media (max-width: 767px) {
                    .chat-input-content {
                        margin: 0;
                        padding: 1rem;
                        border-radius: 0;
                    }
                }
                
                /* Welcome Section - More compact and centered */
                .welcome-section {
                    text-align: center;
                    padding: 3rem 2rem;
                    max-width: 800px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    min-height: calc(100vh - 200px); /* Account for input area */
                }
                
                .welcome-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem;
                    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.2);
                }
                
                .welcome-title {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 0.75rem;
                }
                
                .welcome-subtitle {
                    font-size: 1rem;
                    color: #64748b;
                    margin-bottom: 2rem;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    line-height: 1.6;
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1.5rem;
                    max-width: 700px;
                    margin: 0 auto;
                }
                
                .stat-card {
                    background: white;
                    padding: 1.5rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                }
                
                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                }
                
                .stat-value {
                    font-size: 2rem;
                    margin-bottom: 0.5rem;
                }
                
                .stat-label {
                    font-weight: 600;
                    color: #475569;
                    font-size: 0.8rem;
                }
                
                /* Simple Verification Table */
                .verification-table {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    margin: 1rem 0;
                    overflow: hidden;
                }
                
                .verification-header {
                    background: #f8fafc;
                    padding: 0.75rem 1rem;
                    border-bottom: 1px solid #e2e8f0;
                    font-weight: 600;
                    color: #374151;
                    font-size: 0.875rem;
                }
                
                .verification-info {
                    padding: 0.75rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.875rem;
                    color: #6b7280;
                }
                
                .verification-table .table {
                    margin-bottom: 0;
                    font-size: 0.875rem;
                }
                
                .verification-table .table th {
                    background: #f8fafc;
                    border: none;
                    color: #374151;
                    font-weight: 600;
                    padding: 0.75rem;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .verification-table .table td {
                    border: none;
                    padding: 0.75rem;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .account-number {
                    font-family: monospace;
                    font-weight: 600;
                    color: #1f2937;
                }
                
                .amount-debit {
                    color: #dc2626;
                    font-weight: 600;
                    text-align: right;
                }
                
                .amount-credit {
                    color: #059669;
                    font-weight: 600;
                    text-align: right;
                }
                
                .verification-total {
                    background: #f9fafb;
                    padding: 0.75rem 1rem;
                    border-top: 1px solid #e2e8f0;
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .verification-actions {
                    padding: 1rem;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    gap: 0.5rem;
                }
                
                /* Message Styles */
                .message-bubble {
                    max-width: 80%;
                    margin-bottom: 1.5rem;
                    word-wrap: break-word;
                }
                
                .message-bubble.user {
                    margin-left: auto;
                }
                
                .message-content {
                    padding: 1rem 1.5rem;
                    border-radius: 16px;
                    position: relative;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }
                
                .message-content.user {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    border-bottom-right-radius: 4px;
                }
                
                .message-content.bot {
                    background: white;
                    color: #1e293b;
                    border: 1px solid #e2e8f0;
                    border-bottom-left-radius: 4px;
                }
                
                .message-content.system {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    color: #0c4a6e;
                    border: 1px solid #bae6fd;
                    border-radius: 12px;
                    text-align: center;
                }
                
                .message-content.error {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    color: #dc2626;
                    border: 1px solid #fecaca;
                }
                
                .message-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                
                .message-avatar.user {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    margin-left: auto;
                }
                
                .message-avatar.bot {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                
                .message-avatar.system {
                    background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
                    margin: 0 auto;
                }
                
                /* Input Area Styles */
                .attachment-preview {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }
                
                .attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 20px;
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                    color: #374151;
                }
                
                .attachment-remove {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 1.25rem;
                    line-height: 1;
                    margin-left: 0.5rem;
                    padding: 0;
                }
                
                .attachment-remove:hover {
                    color: #ef4444;
                }
                
                .input-container {
                    position: relative;
                    background: white;
                    border: 2px solid #e2e8f0;
                    border-radius: 16px;
                    transition: all 0.3s ease;
                }
                
                .input-container:focus-within {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                
                .input-textarea {
                    width: 100%;
                    border: none;
                    outline: none;
                    resize: none;
                    padding: 1rem 4rem 1rem 1.5rem;
                    font-size: 1rem;
                    line-height: 1.5;
                    border-radius: 16px;
                    background: transparent;
                    color: #1e293b;
                    min-height: 56px;
                    max-height: 120px;
                }
                
                .input-textarea::placeholder {
                    color: #64748b;
                }
                
                .input-textarea:disabled {
                    background: #f8fafc;
                    color: #64748b;
                }
                
                .input-controls {
                    position: absolute;
                    right: 1rem;
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .input-control-btn {
                    background: none;
                    border: none;
                    color: #64748b;
                    cursor: pointer;
                    padding: 0.5rem;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .input-control-btn:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #3b82f6;
                }
                
                .input-control-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                .send-button {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    border: none;
                    color: white;
                    padding: 1rem;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 56px;
                    height: 56px;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
                }
                
                .send-button:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }
                
                .send-button:disabled {
                    background: #d1d5db;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                
                /* Connection Status - fixed to viewport */
                .connection-status {
                    position: fixed;
                    top: 1rem;
                    right: 1rem;
                    z-index: 999;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    background: white;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e2e8f0;
                }
                
                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                }
                
                .status-dot.connected {
                    background: #10b981;
                }
                
                .status-dot.disconnected {
                    background: #ef4444;
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                
                /* Reset/Settings buttons - fixed to viewport */
                .chat-controls {
                    position: fixed;
                    top: 1rem;
                    left: 1rem;
                    z-index: 999;
                    display: flex;
                    gap: 0.5rem;
                }
                
                .chat-control-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    padding: 0.5rem;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }
                
                .chat-control-btn:hover {
                    background: #f8fafc;
                    color: #3b82f6;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                }
                
                /* Typing Indicator */
                .typing-indicator {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 1rem 1.5rem;
                    margin-bottom: 1rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                    max-width: 200px;
                }
                
                .typing-dots {
                    display: flex;
                    gap: 0.25rem;
                }
                
                .typing-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #64748b;
                    animation: typingBounce 1.4s infinite ease-in-out;
                }
                
                .typing-dot:nth-child(1) { animation-delay: -0.32s; }
                .typing-dot:nth-child(2) { animation-delay: -0.16s; }
                .typing-dot:nth-child(3) { animation-delay: 0; }
                
                @keyframes typingBounce {
                    0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
                    40% { transform: scale(1); opacity: 1; }
                }
                
                /* Message Actions */
                .message-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f1f5f9;
                }
                
                .action-btn {
                    background: none;
                    border: none;
                    color: #64748b;
                    cursor: pointer;
                    padding: 0.5rem;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .action-btn:hover {
                    background: #f1f5f9;
                    color: #3b82f6;
                }
                
                /* Suggestions */
                .suggestion-pills {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                    margin-top: 1rem;
                }
                
                .suggestion-pill {
                    background: rgba(59, 130, 246, 0.1);
                    color: #3b82f6;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-size: 0.875rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 1px solid rgba(59, 130, 246, 0.2);
                }
                
                .suggestion-pill:hover {
                    background: rgba(59, 130, 246, 0.2);
                    transform: translateY(-1px);
                }
            `}</style>

            {/* Fixed Chat Controls */}
            <div className="chat-controls">
                <button
                    className="chat-control-btn"
                    onClick={resetConversation}
                    title="Ny konversation"
                >
                    <RotateCcw size={16} />
                </button>

                <Dropdown>
                    <Dropdown.Toggle as="button" className="chat-control-btn">
                        <Settings size={16} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={startFreshConversation}>
                            <Plus size={16} className="me-2" />
                            Börja om helt
                        </Dropdown.Item>
                        <Dropdown.Item>
                            <Download size={16} className="me-2" />
                            Exportera chatt
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item>
                            <Settings size={16} className="me-2" />
                            Inställningar
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Fixed Connection Status */}
            <div className="connection-status">
                <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
                <span style={{ color: isConnected ? '#10b981' : '#ef4444' }}>
                    {isConnected ? 'Ansluten' : 'Ansluter...'}
                </span>
            </div>

            {/* Chat Container - Regular Page Content */}
            <div className="chat-container">
                {/* Messages Area */}
                <div className="chat-messages">
                    {/* Welcome section - only show when no messages */}
                    {messages.length === 0 && (
                        <div className="welcome-section">
                            <div className="welcome-icon">
                                <Sparkles size={24} color="white" />
                            </div>
                            <h2 className="welcome-title">Välkommen till Ledgy AI Assistant</h2>
                            <p className="welcome-subtitle">
                                Beskriv dina transaktioner i vanlig text så skapar jag automatiskt korrekta verifikat åt dig.
                                Du kan också ladda upp kvitton och bilder för ännu bättre precision.
                            </p>

                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-value">⚡</div>
                                    <div className="stat-label">Snabb & Enkel</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-value">🎯</div>
                                    <div className="stat-label">Exakt Bokföring</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-value">🤖</div>
                                    <div className="stat-label">AI-driven</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Chat Messages */}
                    {messages.map((msg, index) => (
                        <div key={msg.id}>
                            {msg.sender !== 'system' && (
                                <div className={`message-avatar ${msg.sender}`}>
                                    {msg.sender === 'user' ? <User size={20} /> : <Bot size={20} />}
                                </div>
                            )}

                            <div className={`message-bubble ${msg.sender}`}>
                                <div className={`message-content ${msg.sender} ${msg.error ? 'error' : ''}`}>
                                    {msg.message}

                                    {/* Simple Verification Display */}
                                    {msg.verification && (
                                        <div className="verification-table">
                                            <div className="verification-header">
                                                Verifikat förslag
                                            </div>

                                            <div className="verification-info">
                                                {msg.verification.date} • {msg.verification.description}
                                            </div>

                                            {msg.verification.transactions && msg.verification.transactions.length > 0 && (
                                                <>
                                                    <Table className="mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Konto</th>
                                                                <th>Beskrivning</th>
                                                                <th className="text-end">Debet</th>
                                                                <th className="text-end">Kredit</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {msg.verification.transactions.map((transaction, idx) => (
                                                                <tr key={idx}>
                                                                    <td>
                                                                        <span className="account-number">
                                                                            {transaction.account}
                                                                        </span>
                                                                    </td>
                                                                    <td>{transaction.description}</td>
                                                                    <td className={transaction.amount > 0 ? "amount-debit" : ""}>
                                                                        {transaction.amount > 0 ? formatCurrency(transaction.amount) : '—'}
                                                                    </td>
                                                                    <td className={transaction.amount < 0 ? "amount-credit" : ""}>
                                                                        {transaction.amount < 0 ? formatCurrency(Math.abs(transaction.amount)) : '—'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>

                                                    {(() => {
                                                        const totals = calculateVerificationTotals(msg.verification.transactions);
                                                        return (
                                                            <div className="verification-total">
                                                                <span>Status:</span>
                                                                <span style={{ color: totals.balanced ? '#059669' : '#dc2626' }}>
                                                                    {totals.balanced ? '✓ Balanserat' : '⚠ Ej balanserat'}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            )}

                                            <div className="verification-actions">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleVerificationProposal(msg.verification)}
                                                >
                                                    Skapa verifikat
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handleVerificationView(msg.verification)}
                                                >
                                                    Visa
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Suggestions */}
                                    {msg.suggestions && msg.suggestions.length > 0 && (
                                        <div className="suggestion-pills">
                                            {msg.suggestions.map((suggestion, idx) => (
                                                <div
                                                    key={idx}
                                                    className="suggestion-pill"
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                >
                                                    {suggestion}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Message Actions */}
                                {msg.sender === 'bot' && !msg.error && (
                                    <div className="message-actions">
                                        <button className="action-btn" onClick={() => copyToClipboard(msg.message)}>
                                            <Copy size={12} />
                                        </button>
                                        <button className="action-btn">
                                            <ThumbsUp size={12} />
                                        </button>
                                        <button className="action-btn">
                                            <ThumbsDown size={12} />
                                        </button>
                                        <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: 'auto' }}>
                                            {msg.timestamp.toLocaleTimeString('sv-SE', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </span>
                                    </div>
                                )}

                                {msg.sender === 'user' && (
                                    <div className="text-end mt-2">
                                        <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                            {msg.timestamp.toLocaleTimeString('sv-SE', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Typing Indicator */}
                    {isTyping && (
                        <div>
                            <div className="message-avatar bot">
                                <Bot size={20} />
                            </div>
                            <div className="typing-indicator">
                                <div className="typing-dots">
                                    <div className="typing-dot"></div>
                                    <div className="typing-dot"></div>
                                    <div className="typing-dot"></div>
                                </div>
                                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                    Ledgy tänker...
                                </span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Fixed Input Area */}
            <div className="chat-input-wrapper">
                <div className="chat-input-content">
                    {/* Attachments Preview */}
                    {attachments.length > 0 && (
                        <div className="attachment-preview">
                            {attachments.map((attachment) => (
                                <div key={attachment.id} className="attachment-chip">
                                    <FileText size={16} />
                                    <span>{attachment.name}</span>
                                    <span style={{ color: '#64748b' }}>({formatFileSize(attachment.size)})</span>
                                    <button
                                        className="attachment-remove"
                                        onClick={() => removeAttachment(attachment.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Input Container */}
                    <div className="d-flex align-items-end gap-3">
                        <div className="input-container flex-grow-1">
                            <textarea
                                ref={textareaRef}
                                className="input-textarea"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={showingExample ? "" : "Beskriv vad du vill bokföra, eller ställ en fråga..."}
                                disabled={!isConnected || showingExample}
                                rows={1}
                            />

                            <div className="input-controls">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    accept="image/*,.pdf,.doc,.docx,.txt"
                                />

                                <button
                                    className="input-control-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!isConnected}
                                >
                                    <Paperclip size={16} />
                                </button>

                                <button
                                    className="input-control-btn"
                                    disabled={!isConnected}
                                >
                                    <Mic size={16} />
                                </button>
                            </div>
                        </div>

                        <button
                            className="send-button"
                            onClick={sendMessage}
                            disabled={!inputMessage.trim() || !isConnected || showingExample}
                        >
                            <Send size={18} />
                        </button>
                    </div>

                    {/* Footer Info */}
                    <div className="text-center mt-3">
                        <small style={{ color: '#64748b' }}>
                            Tryck Shift+Enter för ny rad • AI kan göra misstag, kontrollera viktiga uppgifter
                        </small>
                    </div>
                </div>
            </div>

            {/* Simple Verification Modal */}
            <Modal
                show={showVerificationModal}
                onHide={() => setShowVerificationModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Skapa verifikat</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {pendingVerification && (
                        <>
                            <Alert variant="info">
                                Granska verifikatet nedan innan det skapas i systemet.
                            </Alert>

                            <div className="mb-3">
                                <strong>Datum:</strong> {pendingVerification.date}<br />
                                <strong>Beskrivning:</strong> {pendingVerification.description}
                            </div>

                            {pendingVerification.transactions && (
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Konto</th>
                                            <th>Beskrivning</th>
                                            <th className="text-end">Debet</th>
                                            <th className="text-end">Kredit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingVerification.transactions.map((transaction, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <span className="account-number">
                                                        {transaction.account}
                                                    </span>
                                                </td>
                                                <td>{transaction.description}</td>
                                                <td className={transaction.amount > 0 ? "amount-debit" : ""}>
                                                    {transaction.amount > 0 ? formatCurrency(transaction.amount) : '—'}
                                                </td>
                                                <td className={transaction.amount < 0 ? "amount-credit" : ""}>
                                                    {transaction.amount < 0 ? formatCurrency(Math.abs(transaction.amount)) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowVerificationModal(false)}
                        disabled={isCreatingVerification}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={createVerification}
                        disabled={isCreatingVerification}
                    >
                        {isCreatingVerification ? 'Skapar...' : 'Skapa verifikat'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default BookkeepingChatPage;